import type { LogoSize, RecipientsOnReportForm } from '@faceup/utils'
import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useContext,
  useState,
} from 'react'

export type PreviewCustomValues = {
  title?: string
  description?: string
  anonymityEnabled?: boolean
  anonymityPreselected?: boolean
  recipientsOnReportForm?: RecipientsOnReportForm
  emailNotificationEnabled?: boolean
  voiceSubmissionEnabled?: boolean
  logoBase64?: string | null
  logoSize?: LogoSize
  reportColor?: string
  categoryId?: string
  // Update the timestamp if you want to force reload the fields.
  // For example, when you add a new field or when you change the order of the fields
  fieldsReloadTimestamp?: number
  source: 'customization'
}

type FollowUpPreviewContextType = {
  previewValues: Readonly<PreviewCustomValues>
  setPreviewValues: Dispatch<SetStateAction<Readonly<PreviewCustomValues>>>
  reloadPreview: () => void
}
const FollowUpPreviewContext = createContext<FollowUpPreviewContextType>({
  setPreviewValues: () => null,
  previewValues: { source: 'customization' },
  reloadPreview: () => null,
})

type FollowUpPreviewProviderProps = {
  children: ReactNode
}

export const FollowUpPreviewProvider = ({ children }: FollowUpPreviewProviderProps) => {
  const [previewValues, setPreviewValues] = useState<PreviewCustomValues>({
    source: 'customization',
  })

  return (
    <FollowUpPreviewContext.Provider
      value={{
        previewValues,
        setPreviewValues,
        reloadPreview: () =>
          setPreviewValues(prevState => ({ ...prevState, fieldsReloadTimestamp: Date.now() })),
      }}
    >
      {children}
    </FollowUpPreviewContext.Provider>
  )
}

export const useFollowUpPreview = () => useContext(FollowUpPreviewContext)
